import type {Router, Route, RouteLocation} from 'src/types/router';
import type {ModalStateEntry} from 'src/types/modal';

import * as React from 'react';
import {connect} from 'react-redux';
import defer from 'lodash/defer';
import flow from 'lodash/flow';
import {classify} from 'src/utils';
import {withHistory} from 'src/flux/withRouter';
import {TitleBarModal} from 'src/components/modals/base-modal';
import {
  TitleBarModal as TitleBarModalDS2021,
  ErrorTitleBarModal,
  WarningTitleBarModal,
  InfoTitleBarModal,
} from 'src/components/modals/base-modal-design-system-2021';

import AudienceCsvUploadModal from 'src/components/modals/audience-csv-upload-modal';
import AudienceCsvUploadErrorModal from 'src/components/modals/audience-csv-upload-error-modal';
import GenericErrorModal from 'src/components/modals/generic-error-modal';
import GenericSidepanel from 'src/components/modals/generic-sidepanel';
import GenericSuccessModal from 'src/components/modals/generic-success-modal';
import WorkflowDeleteModal from 'src/components/modals/workflow-delete-modal';
import OrganizerWorkflowDeleteModal from 'src/components/modals/organizer-workflow-delete-modal';

import EventDeleteModal from 'src/components/modals/event-delete-modal';
import EventAudienceModal from 'src/components/modals/event-audience-modal';
import CreateEventModal from 'src/components/modals/create-event-modal';
import SendTestEmailSmsModal from 'src/components/modals/test-email-sms-modal';
import JobMatchParamsModal from 'src/components/modals/job-match-params-modal';
import ChooseTimeZoneModal from 'src/components/modals/choose-time-zone-modal';
import BulkWritebackActivationModal from 'src/components/modals/bulk-writeback-activation-modal';
import DataReviewSidebar from 'src/components/conversation-builder/data-review-sidepanel';
import FeedBackContainer from 'src/components/modals/feedback';
import ReferralWithdrawContainer from 'src/components/modals/referral-withdraw';
import DefaultNumberForCategory from 'src/components/modals/default-number-for-category-modal';
import NewJourneyBuilder from 'src/components/modals/new-journey-builder-modal';
import AccountEdit from 'src/components/settings/accounts/account-edit';
import ImportEntityModal from 'src/components/modals/import-entity-warning';
import {TearsheetTypeSelectModal} from 'src/components/settings/ats/tearsheets/tearsheets';
import {
  VariablePickerModal,
  JobVariablePickerModal,
} from 'src/components/lib/variable-picker';
import {popModal, removeErrorModal} from 'src/action-creators/modal';
import {selectHead} from 'src/selectors/modal';

import css from './modal-root.css';

// @ts-expect-error - TS2456 - Type alias 'ModalType' circularly references itself.
export type ModalType = keyof typeof MODAL_COMPONENTS;

// @ts-expect-error - TS7022 - 'MODAL_COMPONENTS' implicitly has type 'any' because it does not have a type annotation and is referenced directly or indirectly in its own initializer.
const MODAL_COMPONENTS = {
  AUDIENCE_CSV_UPLOAD: AudienceCsvUploadModal, // UNUSED MODAL
  AUDIENCE_CSV_UPLOAD_ERROR: AudienceCsvUploadErrorModal, // UNUSED MODAL
  GENERIC_ERROR: GenericErrorModal,
  GENERIC_SUCCESS: GenericSuccessModal,
  GENERIC_MODAL: TitleBarModal,
  GENERIC_MODAL_DS_2021: TitleBarModalDS2021,
  GENERIC_ERROR_DS_2021: ErrorTitleBarModal,
  GENERIC_WARNING_DS_2021: WarningTitleBarModal,
  GENERIC_INFO_DS_2021: InfoTitleBarModal,
  WORKFLOW_DELETE: WorkflowDeleteModal,
  ORGANIZER_WORKFLOW_DELETE: OrganizerWorkflowDeleteModal,
  EVENT_DELETE: EventDeleteModal,
  EVENT_AUDIENCE: EventAudienceModal,
  CREATE_EVENT: CreateEventModal,
  TEST_EMAIL_SMS: SendTestEmailSmsModal,
  CREATE_USER_MODAL: AccountEdit,
  CHOOSE_TIME_ZONE: ChooseTimeZoneModal,
  VARIABLE_PICKER: VariablePickerModal,
  JOB_VARIABLE_PICKER: JobVariablePickerModal,
  IMPORT_ENTITY_WARNING: ImportEntityModal,
  TEARSHEET_TYPE_SELECT: TearsheetTypeSelectModal,
  BULK_WRITEBACK_ACTIVATION_MODAL: BulkWritebackActivationModal,
  DATA_REVIEW_SIDEPANEL: DataReviewSidebar,
  JOB_MATCH_PARAMS_MODAL: JobMatchParamsModal,
  GENERIC_SIDEPANEL: GenericSidepanel,
  FEEDBACK_MODAL: FeedBackContainer,
  SELECT_DEFAULT_NUMBER: DefaultNumberForCategory,
  NEW_JOURNEY_BUILDER: NewJourneyBuilder,
  REFERRAL_WITHDRAW: ReferralWithdrawContainer,
} as const;

const mapStateToProps = (state: any) => ({
  head: selectHead(state),
  isError: Boolean(state.modal.error),
});

const mapDispatchToProps = (dispatch: any) => ({
  // @ts-expect-error - TS7006 - Parameter 'isError' implicitly has an 'any' type. | TS7019 - Rest parameter 'args' implicitly has an 'any[]' type.
  removeModal: (isError, ...args) => {
    const actionCreator = isError ? removeErrorModal : popModal;
    dispatch(actionCreator(...args));
  },
});

const mergeProps = (stateProps: any, dispatchProps: any, ownProps: any) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  // @ts-expect-error - TS7019 - Rest parameter 'args' implicitly has an 'any[]' type.
  removeModal: (...args) =>
    dispatchProps.removeModal(stateProps.isError, ...args),
});

type Props = {
  head: ModalStateEntry;
  isError: boolean;
  router: Router;
  location: RouteLocation;
  removeModal: () => void;
};

class ModalRoot extends React.Component<Props> {
  fixBody() {
    const body = document.getElementsByTagName('body')[0];
    if (body && body.classList) {
      body.classList.add('fixed');
    }
  }

  unfixBody() {
    const body = document.getElementsByTagName('body')[0];
    if (body && body.classList) {
      body.classList.remove('fixed');
    }
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, {}, any>'.
  componentDidUpdate() {
    if (this.props.head) {
      this.fixBody();
    } else {
      this.unfixBody();
    }
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, {}, any>'.
  render() {
    const {head} = this.props;

    if (!head) {
      return null;
    }

    let allowClickAway;
    let modal;
    // @ts-expect-error - TS2339 - Property 'render' does not exist on type 'ModalStateEntry'.
    if (head.render) {
      // @ts-expect-error - TS2339 - Property 'allowClickAway' does not exist on type 'ModalStateEntry'.
      allowClickAway = head.allowClickAway ?? true;
      // @ts-expect-error - TS2339 - Property 'render' does not exist on type 'ModalStateEntry'.
      modal = head.render({removeModal: this.props.removeModal});
    } else {
      // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'ModalStateEntry'. | TS2339 - Property 'props' does not exist on type 'ModalStateEntry'.
      const {type, props} = head;
      const SpecificModal =
        typeof type === 'string' ? MODAL_COMPONENTS[type] : type;
      modal = (
        <SpecificModal
          className={css.modal}
          removeModal={this.props.removeModal}
          type={type}
          {...props}
        />
      );
      allowClickAway = props?.allowClickAway ?? true;
    }

    return (
      <div
        className={classify(css.bg, {
          [css.topMost]:
            // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'ModalStateEntry'.
            head.props &&
            // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'ModalStateEntry'.
            head.props.topmost &&
            // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'ModalStateEntry'.
            head.props.modalVersion !== 'genesis',
        })}
        // @ts-expect-error - TS2322 - Type '((event: any) => void) | null' is not assignable to type 'MouseEventHandler<HTMLDivElement> | undefined'.
        onClick={allowClickAway ? this.handleBgClick.bind(this) : null}
      >
        <div className={css.container} role="dialog">
          {modal}
        </div>
      </div>
    );
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, {}, any>'.
  componentDidMount() {
    // @ts-expect-error - TS2769 - No overload matches this call.
    window.addEventListener('keyup', this.handleKeyUp);
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<Props, {}, any>'.
  componentWillUnmount() {
    // @ts-expect-error - TS2769 - No overload matches this call.
    window.removeEventListener('keyup', this.handleKeyUp);
  }

  handleKeyUp = (evt: React.KeyboardEvent<any>) => {
    // Pop modals on escape keypress _only_ when modal is active
    if (this.props.head && evt.key === 'Escape') {
      this.removeModal();
    }
  };
  handleBgClick(event: any) {
    // Must click bg directly. Ignore clicks on children
    if (event.currentTarget !== event.target) {
      return;
    }
    this.removeModal();
  }
  removeModal() {
    const {head, router} = this.props;
    if (!head) {
      return;
    }
    this.props.removeModal();
    // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'ModalStateEntry'. | TS2339 - Property 'props' does not exist on type 'ModalStateEntry'.
    if (router && head.props && head.props.routed) {
      // defer for more responsive modal popping
      defer(() => {
        // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'ModalStateEntry'.
        if (head.props?.back) {
          router.goBack();
        } else {
          // use router.replace instead of router.push because navigating
          // on background click does not seem like an established UX pattern
          router.replace({
            ...router.location,
            // Go up one level in the URL hierarchy.
            // NOTE (gab): we can easily replace `routed` prop with next location
            // if we want to let each modal decide where to navigate on pop
            pathname: router.location.pathname.replace(/\/[^/]+\/?$/, ''),
          });
        }
      });
    }
  }
}

// TODO: (chris) We can get rid of the pure:false option when none of the
// child modal components are getting their data from Flux stores
export default flow(
  connect(mapStateToProps, mapDispatchToProps, mergeProps, {
    pure: false,
  }),
  withHistory,
)(ModalRoot) as React.ComponentType<Record<any, any>>;
